// Event Horizon — custom timed event (custom-events-v1 §E1). A black hole
// tears open at a seeded position ≥30 u from the player and, for 10 s, sucks
// enemies, pickups, projectiles, and the player toward the singularity —
// stronger than gravity-well, with the same 0.75 s ramp in/out envelope.
//
// Mechanics (spec):
// - Enemies within 2 u of the center are CONSUMED: released with a `death`
//   event carrying payload.consumed = true so kill drops are skipped. We
//   deliberately do NOT call killEnemy (no xp/health/crate, no splitter).
//   Boss-flagged enemies are exempt — boss-ai owns bosses; the hole pulls
//   and slows them like everything else but never destroys their entity.
// - Player within 3 u takes 8 dps as a per-tick applyDamageToPlayer trickle
//   (8·dt), gated by dash i-frames and the mercy window. Being a continuous
//   zone it does NOT set mercy_until itself — mercy gates burst hits, the
//   trickle just respects an active one.
// - Space-time warp: entities within 12 u move at 50%, applied as a
//   displacement post-adjust (see post-adjust.ts — the ion-storm pattern;
//   scaling vel would be fought by enemy steering).
//
// Dual-edge by design: it devours the swarm too — baiting enemies in is
// valid play.
//
// Determinism (GDD Appendix A §A.1): the hole position comes from a private
// mulberry32 stream reseeded from world.runSeed in start(); everything else
// is a pure function of world state and tick. No Math.random, no wall-clock.
//
// Perf: pool-order iteration only; consumption collects victims before
// releasing (meteor-shower two-pass pattern); the scalers reuse their entry
// objects, so steady-state update() allocates nothing but damage events.
//
// Visuals (wired by the parent, not here): 'phase_change'
//   { system: 'events', event_id: 'event_horizon', phase: 'start'|'end',
//     danger: 3, pos, radius, duration }

import type { Enemy, Vec2 } from '../../types.js';
import type { WorldState } from '../state.js';
import type { GameEventDef } from '../events-manager.js';
import { RngStream } from '../rng.js';
import { wrapArena, wrappedDelta } from '../collision.js';
import { applyPlayerKnockback } from '../movement.js';
import { applyDamageToPlayer } from '../combat.js';
import { DisplacementScaler, PlayerDisplacementScaler } from './post-adjust.js';

/** Event lifetime: 10 s at 60 Hz. */
const EVENT_DURATION_TICKS = 10 * 60;
/** Pull radius (arena units); gravity-well reaches 70. */
const PULL_RADIUS = 80;
/**
 * Acceleration profile: accel = min(MAX_ACCEL, STRENGTH / max(dist², MIN_DIST²)).
 * Roughly 1.5× gravity-well (6000/48/25) at every range, with a sharper core.
 */
const STRENGTH = 9000;
const MAX_ACCEL = 72;
const MIN_DIST_SQ = 9;
/** Faction gains (gravity-well: 2/1/6). The well must visibly win. */
const ENEMY_FORCE_GAIN = 2.5;
const PROJECTILE_FORCE_GAIN = 1.5;
const PICKUP_FORCE_GAIN = 2;
const PLAYER_KNOCKBACK_GAIN = 8;
/** Ramp in/out window (0.75 s), same envelope as gravity-well. */
const RAMP_TICKS = 45;
/** Enemies this close to the singularity (+ their hitbox) are consumed. */
const CONSUME_RADIUS = 2;
/** Player damage radius and rate. */
const PLAYER_DAMAGE_RADIUS = 3;
const PLAYER_DPS = 8;
/** Space-time warp radius: entities inside move at 50%. */
const WARP_RADIUS = 12;
const WARP_FACTOR = 0.5;
/** Placement: uniform inside the walls, rejection-sampled ≥30 u from the player. */
const MIN_PLAYER_DIST = 30;
const ARENA_MARGIN = 4;
const PLACEMENT_TRIES = 8;
/** 'player_hit' heartbeat while the trickle runs (0.5 s at 60 Hz). */
const PLAYER_HIT_EMIT_INTERVAL = 30;
/** Fixed sim step; events only ever run on the 60 Hz fixed step (game.ts STEP). */
const DT = 1 / 60;
/** Private stream salt: keeps hole placement independent of every other system. */
const HORIZON_STREAM_SALT = 0xE1B10C11;

/**
 * Create the Event Horizon event def. All mutable state lives in this
 * closure and is fully reset in start(), so the def instance is reusable
 * across runs and across multiple triggers within one run.
 */
export function createEventHorizonEvent(): GameEventDef {
  const center: Vec2 = { x: 0, y: 0 };
  let stream = new RngStream(0);
  // One scaler per pool: apply() swaps its position maps per call, so a
  // scaler instance tracks exactly one iterable across ticks.
  const slowEnemies = new DisplacementScaler();
  const slowProjectiles = new DisplacementScaler();
  const slowPickups = new DisplacementScaler();
  const playerSlow = new PlayerDisplacementScaler();

  /** Seeded placement ≥30 u (toroidal) from the player. */
  function pickCenter(world: WorldState): void {
    const half = 100 - ARENA_MARGIN;
    for (let i = 0; i < PLACEMENT_TRIES; i++) {
      const x = stream.next_range(-half, half);
      const y = stream.next_range(-half, half);
      const d = wrappedDelta({ x, y }, world.player.pos);
      if (d.x * d.x + d.y * d.y >= MIN_PLAYER_DIST * MIN_PLAYER_DIST) {
        center.x = x;
        center.y = y;
        return;
      }
    }
    // Deterministic fallback (placement kept failing): 40 u from the player
    // at a seeded angle — always satisfies the ≥30 u rule.
    const angle = stream.next_range(0, Math.PI * 2);
    center.x = world.player.pos.x + Math.cos(angle) * 40;
    center.y = world.player.pos.y + Math.sin(angle) * 40;
    wrapArena(center);
  }

  /** Toward-the-center accel for one entity, same profile as gravity-well. */
  function pull(pos: Vec2, vel: Vec2, gain: number, mult: number): void {
    const d = wrappedDelta(center, pos); // direction from pos → singularity
    const distSq = d.x * d.x + d.y * d.y;
    if (distSq > PULL_RADIUS * PULL_RADIUS || distSq < 1e-4) return;
    const dist = Math.sqrt(distSq);
    const accel = Math.min(MAX_ACCEL, STRENGTH / Math.max(distSq, MIN_DIST_SQ)) * mult * gain;
    vel.x += (d.x / dist) * accel * DT;
    vel.y += (d.y / dist) * accel * DT;
  }

  /** Release enemies inside the consume radius. NO drops, NO killEnemy. */
  function consume(world: WorldState, tick: number): void {
    let victims: Enemy[] | null = null;
    for (const e of world.enemies.alive()) {
      if (e.boss !== undefined) continue; // boss-ai owns bosses: pull/slow only
      const d = wrappedDelta(e.pos, center);
      const r = CONSUME_RADIUS + e.hitbox_radius;
      if (d.x * d.x + d.y * d.y > r * r) continue;
      (victims ??= []).push(e);
    }
    if (!victims) return;
    for (const e of victims) {
      world.enemies.release(e);
      world.events.emit(
        'death',
        {
          entity_id: e.id,
          enemy_id: e.id,
          kind: 'enemy',
          def_id: e.def_id,
          pos: { x: e.pos.x, y: e.pos.y },
          consumed: true // kill drops are skipped for the devoured
        },
        tick
      );
    }
  }

  function withinWarp(p: Vec2): boolean {
    const d = wrappedDelta(p, center);
    return d.x * d.x + d.y * d.y <= WARP_RADIUS * WARP_RADIUS;
  }

  return {
    id: 'event_horizon',
    name: 'Event Horizon',
    duration: EVENT_DURATION_TICKS,

    start(world: WorldState, tick: number): void {
      stream = new RngStream((world.runSeed ^ HORIZON_STREAM_SALT) >>> 0);
      pickCenter(world);
      slowEnemies.reset();
      slowProjectiles.reset();
      slowPickups.reset();
      playerSlow.reset();
      world.events.emit(
        'phase_change',
        {
          system: 'events',
          event_id: 'event_horizon',
          phase: 'start',
          danger: 3,
          pos: { x: center.x, y: center.y },
          radius: PULL_RADIUS,
          duration: EVENT_DURATION_TICKS
        },
        tick
      );
    },

    update(world: WorldState, tick: number, progress: number): void {
      // Smooth strength envelope: ramp in, full, ease out (deterministic —
      // derived from the manager-supplied progress, no wall-clock).
      const elapsed = progress * EVENT_DURATION_TICKS;
      const remaining = EVENT_DURATION_TICKS - elapsed;
      const mult = Math.min(1, elapsed / RAMP_TICKS, remaining / RAMP_TICKS);
      if (mult <= 0) return;

      // 1. Pull: perturb velocities (integrated by the owning systems on the
      //    following tick — same ordering contract as gravity-well).
      const player = world.player;
      if (player.alive) {
        const d = wrappedDelta(center, player.pos);
        const distSq = d.x * d.x + d.y * d.y;
        if (distSq <= PULL_RADIUS * PULL_RADIUS && distSq >= 1e-4) {
          const dist = Math.sqrt(distSq);
          const accel =
            Math.min(MAX_ACCEL, STRENGTH / Math.max(distSq, MIN_DIST_SQ)) *
            mult *
            PLAYER_KNOCKBACK_GAIN;
          applyPlayerKnockback(player, { x: d.x / dist, y: d.y / dist }, accel * DT);
        }
      }
      for (const e of world.enemies.alive()) {
        pull(e.pos, e.vel, ENEMY_FORCE_GAIN, mult);
      }
      for (const p of world.projectiles.alive()) {
        pull(p.pos, p.vel, PROJECTILE_FORCE_GAIN, mult);
      }
      for (const pk of world.pickups.alive()) {
        pull(pk.pos, pk.vel, PICKUP_FORCE_GAIN, mult);
      }

      // 2. Consume enemies at the singularity (before the warp retraction,
      //    so the devoured are never also position-adjusted).
      consume(world, tick);

      // 3. Space-time warp: halve this tick's displacement inside 12 u.
      slowEnemies.apply(world.enemies.alive(), WARP_FACTOR, (e) => withinWarp(e.pos));
      slowProjectiles.apply(world.projectiles.alive(), WARP_FACTOR, (p) => withinWarp(p.pos));
      slowPickups.apply(world.pickups.alive(), WARP_FACTOR, (pk) => withinWarp(pk.pos));

      // 4. Player: trickle damage at the core, then the warp slow.
      if (player.alive) {
        const d = wrappedDelta(player.pos, center);
        const distSq = d.x * d.x + d.y * d.y;
        if (
          distSq <= PLAYER_DAMAGE_RADIUS * PLAYER_DAMAGE_RADIUS &&
          player.dash.iframes_until <= tick &&
          player.mercy_until <= tick
        ) {
          applyDamageToPlayer(player, PLAYER_DPS * DT, tick);
          if (tick % PLAYER_HIT_EMIT_INTERVAL === 0) {
            world.events.emit(
              'player_hit',
              {
                damage: PLAYER_DPS * DT * PLAYER_HIT_EMIT_INTERVAL,
                source: 'event_horizon',
                pos: { x: player.pos.x, y: player.pos.y }
              },
              tick
            );
          }
        }
        playerSlow.apply(player, WARP_FACTOR, (_prev, cur) => withinWarp(cur));
      } else {
        playerSlow.reset();
      }
    },

    end(world: WorldState, tick: number): void {
      slowEnemies.reset();
      slowProjectiles.reset();
      slowPickups.reset();
      playerSlow.reset();
      world.events.emit(
        'phase_change',
        {
          system: 'events',
          event_id: 'event_horizon',
          phase: 'end',
          danger: 3,
          pos: { x: center.x, y: center.y },
          radius: PULL_RADIUS
        },
        tick
      );
    }
  };
}
